/* Streaming Markdown (CHAT-01) via Streamdown (Apache-2.0): tolerant of partial code fences and
   tables while tokens arrive. No per-token animation beyond the caret (motion rule).
   `[n]` markers become CitationChips when `citations` are passed (rule 21).
   Streamdown and its Markdown pipeline (~160 kB gzip) load on first use, not with the app shell: until
   they arrive, a small built-in renderer shows the same text (paragraphs, headings, lists, code, bold,
   italic, links, citation chips), so nothing jumps or goes blank. */
import { lazy, memo, Suspense, useMemo, type ComponentType, type ReactNode } from 'react';
import type { Citation } from '../lib/api-types';
import { CitationChip } from './Citations';
import { cx } from './cx';

export interface MarkdownStreamProps {
  children: string;
  streaming?: boolean;
  className?: string;
  citations?: Citation[];
  onCite?: (c: Citation) => void;
}

const CITE_HREF = '#cite-';

export function linkCitations(md: string): string {
  return md.replace(/\[(\d{1,3})\](?!\()/g, `[$1](${CITE_HREF}$1)`);
}

type StreamdownProps = { mode?: string; isAnimating?: boolean; parseIncompleteMarkdown?: boolean; components?: Record<string, unknown>; children: string };
let streamdownPromise: Promise<{ default: ComponentType<StreamdownProps> }> | null = null;
/** Starts loading Streamdown (idempotent); the shell calls it when the browser is idle. */
export function preloadMarkdown(): Promise<{ default: ComponentType<StreamdownProps> }> {
  return (streamdownPromise ??= import('streamdown').then((m) => ({ default: m.Streamdown as unknown as ComponentType<StreamdownProps> })));
}
const Streamdown = lazy(preloadMarkdown);

type LinkRenderer = (href: string, kids: ReactNode, key: string) => ReactNode;

/* ---------- built-in renderer (fallback while Streamdown loads) ---------- */
function inline(text: string, link: LinkRenderer, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\s][^*]*)\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyBase}-${i++}`;
    if (m[2] ?? m[3]) out.push(<strong key={k}>{m[2] ?? m[3]}</strong>);
    else if (m[4]) out.push(<em key={k}>{m[4]}</em>);
    else if (m[5]) out.push(<code key={k}>{m[5]}</code>);
    else if (m[6] && m[7]) out.push(link(m[7], m[6], k));
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function LiteMarkdown({ text, link }: { text: string; link: LinkRenderer }) {
  const blocks: ReactNode[] = [];
  const lines = text.split('\n');
  let i = 0;
  let b = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const key = `b${b++}`;
    if (/^```/.test(line)) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i]!)) body.push(lines[i++]!);
      i++;
      blocks.push(<pre key={key}><code>{body.join('\n')}</code></pre>);
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = Math.min(6, h[1]!.length + 1);
      const Tag = `h${level}` as 'h2';
      blocks.push(<Tag key={key}>{inline(h[2]!, link, key)}</Tag>);
      i++;
      continue;
    }
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*([-*+]|\d+\.)\s+/, ''));
      const List = ordered ? 'ol' : 'ul';
      blocks.push(<List key={key}>{items.map((t, j) => <li key={j}>{inline(t, link, `${key}-${j}`)}</li>)}</List>);
      continue;
    }
    if (/^\s*\|/.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i]!)) {
        const cells = lines[i++]!.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
      }
      const [head, ...body] = rows;
      blocks.push(
        <table key={key}>
          {head && <thead><tr>{head.map((c, j) => <th key={j}>{inline(c, link, `${key}-h${j}`)}</th>)}</tr></thead>}
          <tbody>{body.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k}>{inline(c, link, `${key}-${j}-${k}`)}</td>)}</tr>)}</tbody>
        </table>,
      );
      continue;
    }
    if (!line.trim()) { i++; continue; }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(```|#{1,6}\s|\s*([-*+]|\d+\.)\s|\s*\|)/.test(lines[i]!)) para.push(lines[i++]!);
    blocks.push(<p key={key}>{inline(para.join('\n'), link, key)}</p>);
  }
  return <>{blocks}</>;
}

export const MarkdownStream = memo(function MarkdownStream({ children, streaming = false, className, citations, onCite }: MarkdownStreamProps) {
  const text = citations?.length ? linkCitations(children) : children;
  const renderLink = useMemo<LinkRenderer>(() => (href, kids, key) => {
    if (href.startsWith(CITE_HREF) && citations?.length) {
      const n = Number(href.slice(CITE_HREF.length));
      const c = citations.find((x) => x.n === n);
      if (c) return <CitationChip key={key} citation={c} onOpen={onCite} />;
    }
    return <a key={key} href={href} target="_blank" rel="noreferrer noopener">{kids}</a>;
  }, [citations, onCite]);
  const components = useMemo(() => {
    if (!citations?.length) return undefined;
    return {
      a: ({ href, children: kids }: { href?: string; children?: ReactNode }) => renderLink(href ?? '', kids, href ?? 'a'),
    };
  }, [citations, renderLink]);
  return (
    <div className={cx('md', streaming && 'streaming', className)}>
      <Suspense fallback={<LiteMarkdown text={text} link={renderLink} />}>
        <Streamdown mode={streaming ? 'streaming' : 'static'} isAnimating={streaming} parseIncompleteMarkdown={streaming} components={components}>
          {text}
        </Streamdown>
      </Suspense>
    </div>
  );
});
